/**
 * Validation Schemas
 * Zod schemas for form validation
 */

import { z } from 'zod';
import { VALIDATION_RULES } from '@/config/app.config';

/**
 * Login Form Schema
 */
export const loginSchema = z.object({
  policyNumber: z
    .string()
    .min(1, 'Policy number is required')
    .max(
      VALIDATION_RULES.policyNumber.maxLength,
      `Policy number must be ${VALIDATION_RULES.policyNumber.maxLength} characters or less`
    )
    .regex(
      VALIDATION_RULES.policyNumber.pattern,
      'Policy number must contain only letters, numbers, and dashes'
    )
    .transform((val) => val.trim().toUpperCase()),

  pin: z
    .string()
    .min(1, 'Reference number is required')
    .regex(VALIDATION_RULES.pin.pattern, 'Reference number must contain only letters and numbers')
    .refine(
      (val) =>
        val.length === VALIDATION_RULES.pin.deductibleLength ||
        val.length === VALIDATION_RULES.pin.auditPremiumLength,
      {
        message: `Reference number must be ${VALIDATION_RULES.pin.deductibleLength} or ${VALIDATION_RULES.pin.auditPremiumLength} characters`,
      }
    )
    .transform((val) => val.trim()),
});

export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Payment Amount Schema
 */
export const paymentAmountSchema = z.object({
  amount: z
    .string()
    .min(1, 'Payment amount is required')
    .transform((val) => parseFloat(val))
    .refine((val) => !isNaN(val), 'Payment amount must be a valid number')
    .refine(
      (val) => val >= VALIDATION_RULES.amount.min,
      `Minimum payment is $${VALIDATION_RULES.amount.min}`
    )
    .refine(
      (val) => val <= VALIDATION_RULES.amount.max,
      `Maximum payment is $${VALIDATION_RULES.amount.max}`
    )
    .refine((val) => {
      // Check if has more than 2 decimal places
      const decimalPlaces = (val.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }, 'Amount cannot have more than 2 decimal places'),
});

export type PaymentAmountFormData = z.infer<typeof paymentAmountSchema>;

/**
 * Billing Form Schema
 * For Cybersource Hosted Checkout - NO card data collected
 * Card details are entered on Cybersource's secure page
 */
export const billingSchema = z.object({
  fullName: z
    .string()
    .min(1, 'Full name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less')
    .transform((val) => val.trim()),

  address: z
    .string()
    .min(1, 'Street address is required')
    .min(5, 'Address must be at least 5 characters')
    .max(100, 'Address must be 100 characters or less')
    .transform((val) => val.trim()),

  city: z
    .string()
    .min(1, 'City is required')
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City must be 50 characters or less')
    .transform((val) => val.trim()),

  state: z
    .string()
    .min(1, 'State is required')
    .regex(/^[A-Z]{2}$/i, 'State must be a valid 2-letter code')
    .transform((val) => val.toUpperCase()),

  zip: z
    .string()
    .min(1, 'ZIP code is required')
    .regex(/^\d{5}(-\d{4})?$/, 'ZIP code must be 5 digits or 5+4 format')
    .transform((val) => val.trim()),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .transform((val) => val.trim().toLowerCase()),

  phone: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^\d{10}$/, 'Phone must be 10 digits (no spaces or dashes)')
    .transform((val) => val.replace(/\D/g, '')), // Remove non-digits

  agreeToTerms: z
    .boolean()
    .refine((val) => val === true, 'You must agree to the terms and conditions'),
});

export type BillingFormData = z.infer<typeof billingSchema>;
